import React, { useState, useEffect, useRef, useCallback } from "react";
import "./EncryptionLab.css";

const API_URL = "https://op5a5iw1c1.execute-api.ap-south-1.amazonaws.com/dev/sql-injection";

async function callApi(payload) {
  const res = await fetch(API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ method: "encrypt", ...payload }),
  });
  const text = await res.text();
  let outer;
  try {
    outer = JSON.parse(text);
  } catch (e) {
    console.error("Raw response:", text);
    throw new Error(`Backend did not return JSON: ${text}`);
  }
  const inner = typeof outer.body === "string" ? JSON.parse(outer.body) : outer.body || outer;
  if (outer.statusCode && outer.statusCode >= 400) {
    throw new Error(inner && inner.error ? inner.error : `Request failed with status ${outer.statusCode}`);
  }
  return inner;
}

// Generate random key
function generateRandomKey(length) {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let key = "";
  for (let i = 0; i < length; i++) {
    key += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return key;
}

export default function EncryptionLab() {
  const [encType, setEncType] = useState("symmetric");
  const [plaintext, setPlaintext] = useState("");
  const [algo, setAlgo] = useState("aes");
  const [key, setKey] = useState("");
  const [terminal, setTerminal] = useState("");
  const [cryptoSpec, setCryptoSpec] = useState(null);
  const [decryptedPlaintext, setDecryptedPlaintext] = useState(null);
  const [isWorking, setIsWorking] = useState(false);
  const terminalRef = useRef(null);
  const prevEncType = useRef(encType);
  const encryptLock = useRef(false);

  // Key requirements
  const keyRequirements = {
    aes: { length: 16, label: "16 chars or Generate it" },
    des: { length: 8, label: "8 chars or Generate it" },
    rsa: { length: 0, label: "Backend-managed" },
  };

  // Available algorithms based on encryption type
  const algoOptions = {
    symmetric: [
      { value: "aes", label: "AES" },
      { value: "des", label: "DES" },
    ],
    asymmetric: [
      { value: "rsa", label: "RSA" },
    ],
  };

  // Scroll terminal to bottom
  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [terminal]);

  // Reset key and algo when encryption type changes
  useEffect(() => {
    if (encType !== prevEncType.current) {
      setKey("");
      setAlgo(encType === "symmetric" ? "aes" : "rsa");
      setTerminal((t) => t + `[*] Switched to ${encType} encryption\n`);
      prevEncType.current = encType;
    }
  }, [encType]);

  const handleGenerateKey = () => {
    if (encType === "asymmetric") {
      setTerminal((t) => t + "[!] RSA keys are managed by the backend.\n");
      return;
    }
    const keyLength = keyRequirements[algo].length;
    const newKey = generateRandomKey(keyLength);
    setKey(newKey);
    setTerminal((t) => t + `[*] Generated ${algo.toUpperCase()} key: ${newKey}\n`);
  };

  const validateInput = () => {
    if (!plaintext) {
      setTerminal((t) => t + "[!] Error: Enter a message to encrypt.\n");
      return false;
    }
    if (algo === "rsa" && plaintext.length > 245) {
      setTerminal((t) => t + "[!] Error: RSA plaintext must be 245 bytes or less.\n");
      return false;
    }
    if (encType === "symmetric") {
      const requiredLength = keyRequirements[algo].length;
      if (!/^[\x00-\x7F]*$/.test(key)) {
        setTerminal((t) => t + `[!] Error: ${algo.toUpperCase()} key must contain only ASCII characters.\n`);
        return false;
      }
      if (key.length !== requiredLength) {
        setTerminal((t) => t + `[!] Error: ${algo.toUpperCase()} key must be ${keyRequirements[algo].label}.\n`);
        return false;
      }
    }
    return true;
  };

  const handleEncrypt = useCallback(async (event) => {
    event.preventDefault(); // Prevent event bubbling
    if (encryptLock.current || isWorking) return;
    encryptLock.current = true;

    // Debug log (remove in production)
    console.log("handleEncrypt called:", { plaintext, algo, timestamp: Date.now() });

    if (!validateInput()) {
      encryptLock.current = false;
      return;
    }
    setTerminal((t) => t + `[*] Encrypting with ${algo.toUpperCase()}...\n`);
    setIsWorking(true);
    try {
      const data = await callApi({
        action: "encrypt",
        algo,
        plaintext,
        key: encType === "symmetric" ? key : null,
      });
      setTerminal((t) => t + (data.terminal || ""));
      setCryptoSpec(data.crypto_spec || null);
      setDecryptedPlaintext(null);
    } catch (e) {
      setTerminal((t) => t + `[!] Error: ${e.message}\n`);
    } finally {
      setIsWorking(false);
      encryptLock.current = false;
    }
  }, [algo, plaintext, key, encType, isWorking]);

  const handleDecrypt = async () => {
    if (!cryptoSpec || !cryptoSpec.ciphertext) {
      setTerminal((t) => t + "[!] Error: No ciphertext to decrypt.\n");
      return;
    }
    if (encType === "symmetric" && !validateInput()) return;
    setTerminal((t) => t + `[*] Decrypting with ${cryptoSpec.algo.toUpperCase()}...\n`);
    setIsWorking(true);
    try {
      const data = await callApi({
        action: "decrypt",
        algo: cryptoSpec.algo,
        ciphertext: cryptoSpec.ciphertext,
        key: encType === "symmetric" ? key : null,
        session_id: cryptoSpec.session_id || null,
      });
      setTerminal((t) => t + (data.terminal || ""));
      setDecryptedPlaintext(data.plaintext || "");
    } catch (e) {
      setTerminal((t) => t + `[!] Error: ${e.message}\n`);
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <div className="encryptionlab-container">
      <h1 className="encryptionlab-title">🔒 Encryption Lab</h1>
      <p className="encryptionlab-sub">Encrypt messages and learn cybersecurity.</p>
      <div className="encryptionlab-grid">
        <div className="panel input-panel">
          <h2>Input</h2>
          <div className="row">
            <div className="dropdown-group">
              <label>Encryption Type</label>
              <select value={encType} onChange={(e) => setEncType(e.target.value)}>
                <option value="symmetric">Symmetric</option>
                <option value="asymmetric">Asymmetric</option>
              </select>
            </div>
            <div className="dropdown-group">
              <label>Algorithm</label>
              <select value={algo} onChange={(e) => setAlgo(e.target.value)}>
                {algoOptions[encType].map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
          <label>Message</label>
          <input
            type="text"
            value={plaintext}
            onChange={(e) => setPlaintext(e.target.value)}
            placeholder="Enter message to encrypt"
            className="encrypt-input"
          />
          <label>Key</label>
          <div className="row">
            <input
              type="text"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder={`Enter ${keyRequirements[algo].label} key`}
              disabled={encType === "asymmetric"}
              className="encrypt-input"
            />
            <button className="generate-btn" onClick={handleGenerateKey} disabled={encType === "asymmetric" || isWorking}>
              Generate
            </button>
          </div>
          <div className="row button-row">
            <button onClick={handleEncrypt} disabled={isWorking || !plaintext}>
              Encrypt
            </button>
            <button
              onClick={() => {
                setPlaintext("");
                setKey("");
                setCryptoSpec(null);
                setDecryptedPlaintext(null);
                setTerminal("");
              }}
              disabled={isWorking}
            >
              Reset
            </button>
          </div>
        </div>
        <div className="panel terminal-panel">
          <h2>Terminal</h2>
          <pre ref={terminalRef} className="terminal">{terminal || "Ready.\n"}</pre>
        </div>
        <div className="panel output-panel">
          <h2>Output</h2>
          {cryptoSpec ? (
            <>
              <div className="crypto-output">
                <div><strong>Algorithm:</strong> {cryptoSpec.algo}</div>
                <div><strong>Key:</strong> {cryptoSpec.key || "Backend-managed (RSA)"}</div>
                <div className="mono"><strong>Ciphertext:</strong> {cryptoSpec.ciphertext}</div>
              </div>
              <div className="row button-row">
                <button className="decrypt-btn" onClick={handleDecrypt} disabled={isWorking || !cryptoSpec.ciphertext}>
                  Decrypt
                </button>
              </div>
              {decryptedPlaintext && (
                <div className="plaintext-output">
                  <div><strong>Plaintext:</strong> <span className="mono">{decryptedPlaintext}</span></div>
                </div>
              )}
            </>
          ) : (
            <p className="no-output">No output yet. Encrypt a message to see results.</p>
          )}
        </div>
      </div>
      <div className="footer-note">Educational tool. Use secure systems in production.</div>
    </div>
  );
}